import { SYSTableDefIds, html } from "../../"
import { T, _HasPermission } from "../../global/globals"
import { HookTablePrepareFn } from "../../global/hooks"
import { SYSBaseElement } from "../sysbaseelement"
import type { TableDataAndDef } from "./table"
import { TableGet, TableRebuild } from "./tableapi"

import type {
  FormFnCan_Visibility,
  HookTablePrepareFn_ReturnType,
  HookTablePrepareFn_Type,
} from "../../global/hooks"

import type { SYSDialog } from "../.."
import { DialogClose, DialogIsOpen } from "../dialog/dialogapi"
import {
  ProcessEnd,
  ProcessIsRunning,
  ProcessStart,
} from "../longrunningprocesstracker/processapi"
import {
  _FilterChangedComparedToDefault,
  _SortChangedComparedToDefault,
} from "./tablerowheadermenu/tablesettings/helpers"
import { _ResetFilters, _ResetSort } from "./helpers"
import { StartScreenUpdate } from "../../tracker/proxyhandler"

type NavType = "header" | "row"

export type NavDef = {
  defId: SYSTableDefIds
  type: NavType
}

export class SYSTableNavControl extends SYSBaseElement {
  props: any
  nav: NavDef
  tableData: TableDataAndDef
  oldDefId: string

  handleFilterDefaultClick = async (e: Event) => {
    e.stopPropagation()
    e.preventDefault()
    await _ResetFilters(this.tableData.def)
    await TableRebuild(this.tableData.def.id, { showProcess: true })
  }

  handleSortDefaultClick = async (e: Event) => {
    e.stopPropagation()
    e.preventDefault()
    _ResetSort(this.tableData.def)
    await TableRebuild(this.tableData.def.id, { showProcess: true })
  }

  handleSetPageClick = (e: Event, page: number) => {
    e.stopPropagation()
    e.preventDefault()
    ProcessStart("SYSTableRebuild", {
      snack: { type: "BusyIndicator" },
      instance: this.tableData.def.id,
    })
    StartScreenUpdate(0)
    setTimeout(() => {
      try {
        this.actions.ovl.internal._TableSetPage({
          paging: this.tableData.def.uiState.paging,
          page: page,
        })
      } finally {
        ProcessEnd("SYSTableRebuild", this.tableData.def.id)
      }
    }, 0)
  }

  handleMoreTableOptionsClick = (e: Event) => {
    let def = this.tableData.def
    this.actions.ovl.internal._TableMoreRow({
      def,
      data: this.tableData.data,
      key: def.uiState.firstVisibleColumn,
    })
  }

  handleAddRowClick = async (e: Event) => {
    e.stopPropagation()
    e.preventDefault()
    // if (this.nav.type === "header") {
    //   this.actions.ovl.internal._TableSelectHeader({
    //     def: this.tableData.def,
    //     data: this.tableData.data,
    //     key: "",
    //   })
    if (DialogIsOpen("SYSTableHeaderMenu", "2")) {
      DialogClose("SYSTableHeaderMenu", { instance: "2" })
    } else {
      DialogClose("SYSTableHeaderMenu", { instance: "1" })
    }
    // }
    let addRes = await this.actions.ovl.table._TableAddRow({
      data: this.tableData.data,
      def: this.tableData.def,
    })
    await this.actions.ovl.table._TableEditRow({
      data: this.tableData.data,
      def: this.tableData.def,
      key: addRes.key,
      initialFocusFieldKey: addRes.fieldKeyToFocus,
    })
  }

  handleRefreshClick = (e: Event) => {
    e.stopPropagation()
    e.preventDefault()
    TableRebuild(this.tableData.def.id, { showProcess: true })
  }

  handleSourceData() {
    let oldNavType = this.nav?.type
    this.nav = this.props(this.state)
    let defId = this.nav.defId
    if (typeof defId === "object") {
      //@ts-ignore
      defId = defId.defId
      this.nav = { defId, type: undefined }
    }
    if (oldNavType) {
      this.nav.type = oldNavType
    }
    if (this.oldDefId !== this.nav.defId) {
      this.oldDefId = this.nav.defId
      if (!this.nav.type) {
        this.nav.type = "row"
      }
      let tb = TableGet(this.nav.defId)
      this.tableData = { def: tb.def, data: tb.dataDef }
    }
  }

  async init() {
    this.handleSourceData()
    await super.init()
  }
  async getUI() {
    this.handleSourceData()
    let navbuttonsclass = "ovl-table-navbuttons"
    let tableDef = this.tableData.def
    let feature = tableDef.features
    let customFnHook =
      this.actions.app.features?.[tableDef.feature]?.hooks?.[HookTablePrepareFn]
    let customFnHookResult: HookTablePrepareFn_ReturnType

    let filterDefaultButton = null
    if (
      (_FilterChangedComparedToDefault(tableDef) ||
        tableDef.uiState.filter.filter.forcedFilterKeys) &&
      (this.nav.type === "header" || feature.showFooterButtons)
    ) {
      let filterClearTooltip = T("SYSFilterReset")
      filterDefaultButton = html`
        <button
          title="${filterClearTooltip}"
          @click="${(e) => this.handleFilterDefaultClick(e)}"
          class="fd-button ${navbuttonsclass} __filteractive"
        >
          <i class="sap-icon--clear-filter"></i>
        </button>
      `
    }

    let sortDefaultButton = null
    if (
      _SortChangedComparedToDefault(tableDef) &&
      (this.nav.type === "header" || feature.showFooterButtons)
    ) {
      let sortClearTooltip = T("SYSSortReset")
      sortDefaultButton = html`
        <button
          title="${sortClearTooltip}"
          @click="${(e) => this.handleSortDefaultClick(e)}"
          class="fd-button ${navbuttonsclass} "
        >
          <i class="sap-icon--sort"></i>
        </button>
      `
    }

    let refresh = null
    if (
      this.nav.type === "row" &&
      feature.showRefreshButton.indexOf("TableNav") > -1 &&
      !ProcessIsRunning("SYSTableRebuild") &&
      this.tableData.def.uiState.rebuild.needsRefresh === true &&
      feature.showFooterButtons
    ) {
      refresh = html`
        <button
          @click=${(e) => this.handleRefreshClick(e)}
          class="fd-button ${navbuttonsclass} "
          title="${T("SYSTableRebuild")}"
        >
          <i class="sap-icon--refresh"></i>
        </button>
      `
    }

    let add
    if (
      _HasPermission(tableDef.permission, "A") &&
      feature.add &&
      (this.nav.type === "header" ||
        (this.nav.type === "row" && feature.showFooterButtons))
    ) {
      let values = {
        Add: {
          description: `${T(
            tableDef.title.entityTitle.entityNameTranslationKey
          )}: ${T("SYSAdd")}`,
        },
      }

      if (customFnHook) {
        customFnHookResult = customFnHook(<HookTablePrepareFn_Type>{
          data: this.tableData.data,
          def: tableDef,
          values,
          mode: "HeaderMenu",
          type: "Table",
          columnKey: undefined,
        })
      }

      let visibility: FormFnCan_Visibility = "Visible"
      let icon = "sap-icon--add"
      let disabled = false
      let tooltip = `${T(
        this.tableData.def.title.entityTitle.entityNameTranslationKey
      )}: ${T("SYSAdd")}`
      if (customFnHookResult?.OvlAdd) {
        if (customFnHookResult.OvlAdd.icon) {
          icon = customFnHookResult.OvlAdd.icon
        }
        if (customFnHookResult.OvlAdd.visibility) {
          visibility = customFnHookResult.OvlAdd.visibility
        }
        if (visibility === "Disabled") {
          disabled = true
        }
      }
      if (visibility !== "NotVisible") {
        add = html`
          <button
            @click=${(e) => this.handleAddRowClick(e)}
            class="fd-button ${navbuttonsclass}"
            id="ovl_nav_add${this._id}"
            title="${tooltip}"
            ?disabled=${disabled}
          >
            <i class="${icon}"></i>
          </button>
        `
      }
    }
    let more = null
    if (
      this.nav.type === "row" &&
      feature.showFooterButtons &&
      feature.showMoreButton &&
      (feature.filter || feature.multiSelect) &&
      feature.headerMenu
    ) {
      more = html`
        <button
          title="${T("SYSTableMenu")}"
          @click="${(e) => this.handleMoreTableOptionsClick(e)}"
          class="fd-button ${navbuttonsclass} "
          id="ovl_nav_more${this._id}"
        >
          <i class="sap-icon--overflow"></i>
        </button>
      `
    }

    let tablenav

    let paging = this.tableData.def.uiState.paging
    let dataFilteredAndSorted = this.tableData.def.uiState.dataFilteredAndSorted
    if (feature.page) {
      //console.log(this.tableData.def.id)
      let rebuildInProcess = ProcessIsRunning(
        "SYSTableRebuild",
        this.tableData.def.id
      )
      let count = dataFilteredAndSorted.length
      if (count > paging.pageSize) {
        let pages = Math.ceil(count / paging.pageSize)
        tablenav = html`
          <div
            class="fd-segmented-button"
            role="group"
            aria-label="Tablecontrol"
          >
            <button
              title="Erster Datensatz"
              ?disabled=${paging.page === 0 || pages === 1 || rebuildInProcess}
              @click=${(e) => this.handleSetPageClick(e, 0)}
              class=${`fd-button ${navbuttonsclass}`}
            >
              <i class="sap-icon--media-rewind"></i>
            </button>
            <button
              ?disabled=${paging.page === 0 || rebuildInProcess}
              title="Vorheriger Datensatz"
              @click=${(e) => this.handleSetPageClick(e, paging.page - 1)}
              class=${`fd-button ${navbuttonsclass}`}
            >
              <i class="sap-icon--media-reverse"></i>
            </button>
            <button
              class=${`fd-button ${navbuttonsclass} ovl-table-navbuttons-info`}
            >
              ${paging.page + 1}/${pages}
            </button>
            <button
              title="Nächster Datensatz"
              @click=${(e) => this.handleSetPageClick(e, paging.page + 1)}
              ?disabled=${paging.page + 1 === pages || rebuildInProcess}
              class=${`fd-button ${navbuttonsclass}`}
            >
              <i class="sap-icon--media-play"></i>
            </button>
            <button
              title="Letzter Datensatz"
              @click=${(e) => this.handleSetPageClick(e, pages - 1)}
              ?disabled=${paging.page + 1 === pages ||
              pages === 1 ||
              rebuildInProcess}
              class=${`fd-button ${navbuttonsclass}`}
            >
              <i class="sap-icon--media-forward"></i>
            </button>
          </div>
        `
      }
    }
    let autoCloseButtonIfInDialog

    if (
      this.nav.type === "row" &&
      tableDef.features.autoCloseButtonIfInsideDialog
    ) {
      let dialogEl = <SYSDialog>(<any>this.closest(".ovl-dialog"))
      if (dialogEl) {
        //@ts-ignore
        let ids = dialogEl.id.split("-")
        let dialogType = ids[2]
        let dialogInstance = ids[3]
        autoCloseButtonIfInDialog = html` <button
          @click="${(e) =>
            DialogClose(dialogType, { instance: dialogInstance })}"
          title="${T("SYSCloseDialog")}"
          class="fd-button fd-button--positive ovl-margin-corr2"
        >
          <i class="sap-icon--decline"></i>
        </button>`
      }
    }
    return html` ${tablenav} ${filterDefaultButton} ${sortDefaultButton} ${add}
    ${refresh} ${more} ${autoCloseButtonIfInDialog}`
  }
}
